import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  useCreateTrackMutation,
  useExtractAudioMetadataMutation,
  useAddTrackToPlaylistMutation,
} from "@queries/trackQueries";
import {
  getPresignedUploadUrl,
  uploadFileToPresignedUrl,
} from "@apis/trackApis";
import { useAuthCurator } from "@src/hooks/useAuth";
import { useAppSelector } from "@src/redux/hooks";
import { buildMediaUrl } from "@src/utils/mediaUrl";

export type ExtendedFile = File & {
  uploaded: boolean;
};
type StartUploadProps = {
  newFiles: FileList;
  curatorId: string;
  playlistId?: string;
};
type FileUploadContextType = {
  uploading: boolean;
  progress: number;
  files?: FileList;
  error: string | null;
  startUpload: (props: StartUploadProps) => void;
  setProgress: (progress: number) => void;
  setError: (error: string) => void;
  completeUpload: () => void;
  showProgressModal: boolean;
  setShowProgressModal: (show: boolean) => void;
  completeUploadBool: boolean;
  filesCompletedCount: number;
  validatingFiles: boolean;
  setValidatingFiles: (validating: boolean) => void;
  cancelUpload: () => void;
};

const LARGE_FILE_DURATION_FALLBACK_BYTES = 50 * 1024 * 1024; // 50MB
const FileUploadContext = createContext<FileUploadContextType | undefined>(
  undefined
);

export const FileUploadProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const curator = useAuthCurator();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgressState] = useState(0);
  const [files, setFiles] = useState<FileList>();
  const [error, setErrorState] = useState<string | null>(null);
  const [completeUploadBool, setCompleteUploadBool] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [filesCompletedCount, setFilesCompletedCount] = useState(0);
  const [validatingFiles, setValidatingFiles] = useState<boolean>(false);
  const [cancelRequested, setCancelRequested] = useState(false);
  const cancelRequestedRef = useRef(false);
  const createTrackMutation = useCreateTrackMutation();
  const extractAudioMetadataMutation = useExtractAudioMetadataMutation();
  const addTrackToPlaylistMutation = useAddTrackToPlaylistMutation();
  const jwt = useAppSelector((state) => state.auth.token);

  const getDurationFromAudioFile = (
    file: File
  ): Promise<number | undefined> => {
    return new Promise((resolve) => {
      const audio = document.createElement("audio");
      const objectUrl = URL.createObjectURL(file);
      let settled = false;

      const cleanup = (): void => {
        audio.removeAttribute("src");
        audio.load();
        URL.revokeObjectURL(objectUrl);
      };

      const settle = (duration?: number): void => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(duration);
      };

      const timeoutId = window.setTimeout(() => {
        settle(undefined);
      }, 12000);

      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        window.clearTimeout(timeoutId);
        const parsedDuration = Number(audio.duration);
        if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) {
          settle(undefined);
          return;
        }
        settle(parsedDuration);
      };

      audio.onerror = () => {
        window.clearTimeout(timeoutId);
        settle(undefined);
      };

      audio.src = objectUrl;
    });
  };

  useEffect(() => {
    cancelRequestedRef.current = cancelRequested;
  }, [cancelRequested]);

  type UploadTracksSequentiallyType = {
    files: FileList;
    onProgressUpdate: (progress: number) => void;
    playlistId?: string;
    curatorId: string;
  };
  const uploadTracksSequentially = async ({
    files,
    onProgressUpdate,
    curatorId,
    playlistId,
  }: UploadTracksSequentiallyType): Promise<void> => {
    const totalSteps = files.length * 5;
    let completedSteps = 0;
    const MAX_CONCURRENT_UPLOADS = 2;

    const failedFiles: string[] = [];

    const updateProgress = (): void => {
      const progress = (completedSteps / totalSteps) * 100;
      onProgressUpdate(progress);
    };

    const processFile = async (file: File): Promise<void> => {
      if (cancelRequestedRef.current) return;
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
      const sanitizedFile = new File([file], sanitizedFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      try {
        if (cancelRequestedRef.current) return;

        // Step 1: Get Presigned URL
        const { uploadUrl, fileUrl, headers } = await getPresignedUploadUrl({
          file: sanitizedFile,
          curatorId: curatorId || curator?.documentId || "",
        });

        if (!fileUrl) {
          throw new Error("File URL is missing in response");
        }

        completedSteps += 1;
        updateProgress();
        if (cancelRequestedRef.current) return;

        // Step 2: Upload to S3
        await uploadFileToPresignedUrl({
          file: sanitizedFile,
          uploadUrl,
          headers,
        });

        completedSteps += 1;
        updateProgress();
        if (cancelRequestedRef.current) return;

        const audioData = {
          url: fileUrl,
          size: Math.floor(sanitizedFile.size / 1024),
          mime: sanitizedFile.type || "audio/mpeg",
          ext: sanitizedFile.name.split(".").pop() || "mp3",
          name: sanitizedFile.name,
        };

        const uploadedFileUrl = buildMediaUrl(fileUrl);
        if (!uploadedFileUrl) {
          throw new Error("Failed to resolve uploaded audio URL");
        }

        let metadata: Record<string, unknown> = {};
        let fallbackDuration: number | undefined;
        try {
          const { data: extractionResponse } =
            await extractAudioMetadataMutation.mutateAsync({
              trackUrl: uploadedFileUrl,
              imagesUploadPath: curatorId || curator?.documentId || "",
              jwt: jwt || "",
            });

          if (extractionResponse) {
            const parsedLength =
              extractionResponse.length !== undefined &&
              extractionResponse.length !== null
                ? Number(extractionResponse.length)
                : NaN;
            const durationFromExtraction =
              Number.isFinite(parsedLength) && parsedLength > 0
                ? parsedLength / 1000
                : undefined;

            metadata = {
              title: extractionResponse.title,
              artist: extractionResponse.artist,
              album: extractionResponse.album,
              year:
                extractionResponse.year !== undefined &&
                extractionResponse.year !== null
                  ? String(extractionResponse.year)
                  : undefined,
              duration: durationFromExtraction,
              bitrate: extractionResponse.bitrate,
              sampleRate: extractionResponse.sample_rate,
              albumCoverUrl: extractionResponse.images?.[0],
              waveformUrl: extractionResponse.waveform,
              // The gateway returns the compact binary waveform as
              // `waveform_binary`; older extractors omit it [ROVR-3829].
              waveformBinaryUrl:
                extractionResponse.waveform_binary ??
                extractionResponse.waveformBinary,
            };
          }
          completedSteps += 1;
          updateProgress();
        } catch (extractionError) {
          // eslint-disable-next-line no-console
          console.error(
            `Error extracting metadata for ${file.name}:`,
            extractionError
          );
          // Metadata extraction failed, but we continue with empty metadata
        }

        const shouldUseLargeFileFallback =
          file.size >= LARGE_FILE_DURATION_FALLBACK_BYTES;
        const hasInvalidDuration =
          typeof metadata.duration !== "number" ||
          !Number.isFinite(metadata.duration) ||
          metadata.duration <= 0;

        if (shouldUseLargeFileFallback && hasInvalidDuration) {
          fallbackDuration = await getDurationFromAudioFile(file);
        }

        if (fallbackDuration && fallbackDuration > 0) {
          metadata = {
            ...metadata,
            duration: fallbackDuration,
          };
        }

        if (cancelRequestedRef.current) return;

        const createTrackResponse = await createTrackMutation.mutateAsync(
          {
            ownerId: curatorId || curator?.documentId || "",
            trackName: file?.name ?? "track_file",
            playlistId: playlistId ?? undefined,
            metadata,
            audioData: audioData,
          },
          {
            onSuccess: () => {
              completedSteps += 1;
              updateProgress();
            },
          }
        );
        if (playlistId) {
          const createdTrackDocumentId =
            createTrackResponse?.data?.data?.documentId;
          if (createdTrackDocumentId) {
            await addTrackToPlaylistMutation.mutateAsync(
              {
                trackId: createdTrackDocumentId,
                playlistId,
              },
              {
                onSuccess: () => {
                  completedSteps += 1;
                  updateProgress();
                },
              }
            );
          } else {
            throw new Error("Track created without documentId");
          }
        } else {
          completedSteps += 1;
          updateProgress();
        }

        if (cancelRequestedRef.current) return;
        setFilesCompletedCount((prev) => prev + 1);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error processing file ${file.name}:`, error);
        failedFiles.push(file.name);
      }
    };

    const queue = Array.from(files);
    const workers = Array.from({
      length: Math.min(MAX_CONCURRENT_UPLOADS, queue.length),
    }).map(async () => {
      while (queue.length > 0 && !cancelRequestedRef.current) {
        const nextFile = queue.shift();
        if (!nextFile) return;
        await processFile(nextFile);
      }
    });

    await Promise.all(workers);

    if (!cancelRequestedRef.current) {
      if (failedFiles.length > 0) {
        setError(`Failed to upload: ${failedFiles.join(", ")}`);
      } else {
        completeUpload();
      }
    }
  };

  const startUpload = async ({
    curatorId,
    newFiles,
    playlistId,
  }: {
    newFiles: FileList;
    curatorId: string;
    playlistId?: string;
  }): Promise<void> => {
    setCancelRequested(false);
    cancelRequestedRef.current = false;
    setShowProgressModal(true);
    setCompleteUploadBool(false);
    setUploading(true);
    setFiles(newFiles);
    setProgressState(0);
    setErrorState(null);
    setFilesCompletedCount(0);
    try {
      await uploadTracksSequentially({
        files: newFiles,
        onProgressUpdate: setProgress,
        playlistId,
        curatorId,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error in startUpload:", e);
      setError("Error uploading files");
    }
  };

  const setProgress = (progress: number): void => {
    setProgressState(progress);
  };

  const setError = (error: string): void => {
    setUploading(false);
    setProgressState(0);
    setErrorState(error);
    setCompleteUploadBool(true);
  };

  const completeUpload = (): void => {
    setUploading(false);
    setProgressState(100);
    setCompleteUploadBool(true);
  };

  const cancelUpload = (): void => {
    cancelRequestedRef.current = true;
    resetState();
    setCancelRequested(true);
    setErrorState("Upload cancelled");
  };

  const resetState = (): void => {
    setUploading(false);
    setProgressState(0);
    setFiles(undefined);
    setErrorState(null);
    setCompleteUploadBool(false);
    setShowProgressModal(false);
    setFilesCompletedCount(0);
  };

  return (
    <FileUploadContext.Provider
      value={{
        uploading,
        progress,
        files,
        error,
        startUpload,
        setProgress,
        setError,
        completeUpload,
        showProgressModal,
        setShowProgressModal,
        completeUploadBool,
        filesCompletedCount,
        validatingFiles,
        setValidatingFiles,
        cancelUpload,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = (): FileUploadContextType => {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("useFileUpload must be used within a FileUploadProvider");
  }
  return context;
};
